/**
 * API client for the guest (shared-listing) area.
 *
 * Deliberately separate from lib/api.ts rather than a flag on it. That client
 * treats *any* 401 as "the member session died": it deletes localStorage.token
 * and hard-redirects to /sign-in. Routing guest traffic through it would log a
 * member out of another tab whenever a guest session lapsed, and would bounce
 * guests — who have no account — onto a sign-in page they can do nothing with.
 *
 * The guest token lives under its own key so a member and a guest session can
 * coexist in one browser without either clobbering the other.
 */
import { ApiError } from "@/lib/api";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";

export const GUEST_TOKEN_KEY = "guest_token";
export const GUEST_EMAIL_KEY = "guest_email";
export const GUEST_NAME_KEY = "guest_name";

export function getGuestToken(): string | null {
  try {
    return localStorage.getItem(GUEST_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function storeGuestSession(token: string, email: string, name: string | null): void {
  try {
    localStorage.setItem(GUEST_TOKEN_KEY, token);
    localStorage.setItem(GUEST_EMAIL_KEY, email);
    if (name) localStorage.setItem(GUEST_NAME_KEY, name);
    else localStorage.removeItem(GUEST_NAME_KEY);
  } catch {
    /* private browsing — the session simply won't survive a reload */
  }
}

export function clearGuestSession(): void {
  try {
    localStorage.removeItem(GUEST_TOKEN_KEY);
    localStorage.removeItem(GUEST_EMAIL_KEY);
    localStorage.removeItem(GUEST_NAME_KEY);
  } catch {
    /* ignore */
  }
}

/** Fired when the guest session lapses so GuestContext can drop its state. */
export const GUEST_SESSION_ENDED_EVENT = "guest-session-ended";

function endGuestSession(): void {
  clearGuestSession();
  window.dispatchEvent(new Event(GUEST_SESSION_ENDED_EVENT));
}

async function request<T>(url: string, options?: RequestInit, authed = true): Promise<T> {
  const token = authed ? getGuestToken() : null;
  const res = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  // Expired or revoked guest session. Clear it and let the page re-prompt —
  // never redirect to the member sign-in page.
  if (res.status === 401 && token) {
    endGuestSession();
    throw new ApiError(401, "Your access has expired. Please open your link again.");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = (body as { detail?: unknown }).detail;
    const message =
      typeof detail === "string"
        ? detail
        : typeof detail === "object" && detail !== null && typeof (detail as { message?: string }).message === "string"
        ? (detail as { message: string }).message
        : `Request failed (${res.status})`;

    // The recipient registered an account after the share went out — the guest
    // path is closed to them by design, so surface it as its own case.
    if (res.status === 403 && typeof detail === "object" && detail !== null &&
        (detail as { code?: string }).code === "account_exists") {
      endGuestSession();
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export const guestApi = {
  get: <T>(url: string) => request<T>(url),
  post: <T>(url: string, data: unknown) =>
    request<T>(url, { method: "POST", body: JSON.stringify(data) }),
  /** Pre-verification calls — no guest token exists yet. */
  postPublic: <T>(url: string, data: unknown) =>
    request<T>(url, { method: "POST", body: JSON.stringify(data) }, false),
};
